import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Chip,
  ListItemButton,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import dayjs from "dayjs";
import {
  listPasskeys,
  getPasskeyRegistrationOptions,
  createPasskey,
  deletePasskey,
  renamePasskey,
} from "../../../../utils/passkeys/passkeysApi";
import { createPasskeyCredential } from "../../../../utils/passkeys/passkeys";
import PasskeyCancelledError from "../../../../utils/passkeys/passkeyCancelledError";
import { isPasskeySupported } from "../../../../utils/passkeys/webauthn";
import { useSnackAlert } from "../../../../contexts/SnackAlertContext";

// Keep in sync with accountSecurity.passkeys in public/locales/en/common.json
// -- see the `translate` prop note in ./index.jsx.
const EN = {
  label: "Passkeys",
  supportedHelper:
    "Sign in without a password using Touch ID, Face ID, Windows Hello, or a security key.",
  unsupportedHelper: "Passkeys aren't supported in this browser.",
  add: "Add passkey",
  none: "No passkeys configured.",
  configured: ({ count }) =>
    `${count} passkey${count > 1 ? "s" : ""} configured`,
  configuredChip: "Configured",
  nameDialogTitle: "Name this passkey",
  nickname: "Nickname",
  nicknamePlaceholder: "e.g. MacBook Touch ID",
  continue: "Continue",
  edit: ({ nickname }) => `Edit ${nickname}`,
  remove: ({ nickname }) => `Remove ${nickname}`,
  lastUsed: ({ date }) => `Last used ${date}`,
  added: ({ date }) => `Added ${date}`,
  addSuccess: "Passkey added.",
  addError: "Failed to add passkey.",
  loadError: "Failed to load passkeys.",
  removeSuccess: "Passkey removed.",
  removeError: "Failed to remove passkey.",
  renameSuccess: "Passkey renamed.",
  renameError: "Failed to rename passkey.",
};

export default function Passkeys({
  helperTextStyle,
  translate = true,
  onChange,
}) {
  const { t: i18nT } = useTranslation("common", {
    keyPrefix: "accountSecurity.passkeys",
  });
  const { t: tCommonI18n } = useTranslation("common");
  const t = (key, options) => {
    if (translate) return i18nT(key, options);
    const entry = EN[key];
    return typeof entry === "function" ? entry(options) : entry;
  };
  const tCommon = (key) => (translate ? tCommonI18n(key) : "Cancel");
  const { setSnackAlert } = useSnackAlert();
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [nicknameDialogOpen, setNicknameDialogOpen] = useState(false);
  const [nickname, setNickname] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [editingPasskey, setEditingPasskey] = useState(null);

  const supported = isPasskeySupported();

  const refresh = useCallback(async () => {
    try {
      setPasskeys(await listPasskeys());
    } catch (e) {
      setSnackAlert(t("loadError"), "error");
    } finally {
      setLoading(false);
    }
  }, [setSnackAlert]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAddPasskey = async () => {
    setNicknameDialogOpen(false);
    setAdding(true);
    try {
      const options = await getPasskeyRegistrationOptions();
      const credential = await createPasskeyCredential(options);
      await createPasskey(credential, nickname.trim());
      setSnackAlert(t("addSuccess"), "success");
      setNickname("");
      await refresh();
      onChange?.();
    } catch (e) {
      if (!(e instanceof PasskeyCancelledError)) {
        setSnackAlert(t("addError"), "error");
      }
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePasskey(id);
      setPasskeys((prev) => prev.filter((passkey) => passkey.id !== id));
      setSnackAlert(t("removeSuccess"), "success");
      onChange?.();
    } catch (e) {
      setSnackAlert(t("removeError"), "error");
    }
  };

  const handleEdit = (passkey) => {
    setEditingPasskey(passkey);
    setNickname(passkey.nickname);
    setNicknameDialogOpen(true);
  };

  const handleRename = async () => {
    if (!editingPasskey) return;
    try {
      await renamePasskey(editingPasskey.id, nickname.trim());
      setSnackAlert(t("renameSuccess"), "success");
      setNicknameDialogOpen(false);
      setNickname("");
      await refresh();
    } catch (e) {
      setSnackAlert(t("renameError"), "error");
    } finally {
      setEditingPasskey(null);
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mt: 3,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ width: "60%" }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="body1">{t("label")}</Typography>
            {!loading && passkeys.length === 0 && (
              <Chip
                label={t("none")}
                variant="outlined"
                color="warning"
                size="small"
              />
            )}
            {!loading && passkeys.length > 0 && (
              <Chip
                label={t("configuredChip")}
                variant="outlined"
                color="success"
                size="small"
              />
            )}
          </Stack>
          <Typography variant="body2" sx={helperTextStyle}>
            {supported ? t("supportedHelper") : t("unsupportedHelper")}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          disabled={!supported || adding}
          onClick={() => setNicknameDialogOpen(true)}
        >
          {adding ? <CircularProgress size={16} /> : t("add")}
        </Button>
      </Stack>

      {passkeys.length > 0 && (
        <List dense disablePadding sx={{ mt: 1 }}>
          <ListItemButton
            onClick={() => setListOpen((prev) => !prev)}
            disableRipple
            sx={{
              p: 0,
              width: "29%",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            <ListItemText
              primary={t("configured", { count: passkeys.length })}
            />
            {listOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={listOpen} timeout="auto" unmountOnExit>
            {passkeys.map((passkey) => (
              <ListItem
                key={passkey.id}
                disableGutters
                secondaryAction={
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      edge="end"
                      aria-label={t("edit", { nickname: passkey.nickname })}
                      onClick={() => handleEdit(passkey)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label={t("remove", { nickname: passkey.nickname })}
                      onClick={() => handleDelete(passkey.id)}
                    >
                      <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                }
              >
                <ListItemText
                  primary={passkey.nickname}
                  secondary={
                    passkey.last_used_at
                      ? t("lastUsed", {
                          date: dayjs(passkey.last_used_at).format(
                            "MMM D, YYYY"
                          ),
                        })
                      : t("added", {
                          date: dayjs(passkey.created_at).format("MMM D, YYYY"),
                        })
                  }
                />
              </ListItem>
            ))}
          </Collapse>
        </List>
      )}

      <Dialog
        open={nicknameDialogOpen}
        onClose={() => setNicknameDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("nameDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t("nickname")}
            placeholder={t("nicknamePlaceholder")}
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNicknameDialogOpen(false)}>
            {tCommon("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={editingPasskey ? handleRename : handleAddPasskey}
            disabled={!nickname.trim()}
          >
            {t("continue")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
